/**
 * Socket.IO への接続を扱うフック。
 *
 * 参加者はモバイル回線を使うため、切断は日常的に起きる前提で作っている。
 *   - 自動再接続（Socket.IO の標準機能）
 *   - WebSocket が確立できない環境ではポーリングへ自動的に落ちる
 *   - 再接続時はサーバーが sync:full を送るので、切断中の変動を取り込める
 *   - 接続状態を返し、画面に「オフライン」を表示できるようにする
 */

import { useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import type {
  ActivityPayload,
  EventStagePayload,
  QuestionUnlockedPayload,
  ScoreUpdatePayload,
  SyncFullPayload,
  TimeTickPayload,
} from "./types.js";

export interface RealtimeHandlers {
  onScore(payload: ScoreUpdatePayload): void;
  onActivity(payload: ActivityPayload): void;
  onQuestionUnlocked(payload: QuestionUnlockedPayload): void;
  onEventStage(payload: EventStagePayload): void;
  /** 残り時間の補正。制限時間を設定しているイベントにだけ届く */
  onTimeTick(payload: TimeTickPayload): void;
  onSyncFull(payload: SyncFullPayload): void;
}

export interface RealtimeState {
  /** Socket.IO の接続が確立しているか */
  connected: boolean;
  /** ブラウザがネットワークに接続しているか */
  browserOnline: boolean;
  /** 画面復帰時などに明示的な再同期を要求する */
  resync(): void;
}

export function useRealtime(enabled: boolean, handlers: RealtimeHandlers): RealtimeState {
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  /**
   * ブラウザ自身のネットワーク状態。
   *
   * Socket.IO の切断検知はハートビート待ちのため十数秒かかる。
   * 機内モードや圏外はブラウザが即座に検知できるので、
   * 両方を見ることで「通信できないのに気づかない」時間を短くする。
   */
  const [browserOnline, setBrowserOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  useEffect(() => {
    const goOnline = (): void => setBrowserOnline(true);
    const goOffline = (): void => setBrowserOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // ハンドラは毎レンダーで作り直されるため、参照だけを更新する。
  // これがないと接続が張り直されてしまう。
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) return;

    const socket = io({
      path: "/socket.io",
      // 既定どおり polling で開始し、可能なら WebSocket へ切り替える。
      // 会場のネットワークが WebSocket を通さない場合でも通信を維持できる。
      withCredentials: true,
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5_000,
      // 上限を設けない。電波が戻れば必ず復帰させたいため。
      reconnectionAttempts: Infinity,
      timeout: 10_000,
    });
    socketRef.current = socket;

    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", () => setConnected(false));

    socket.on("score:update", (p: ScoreUpdatePayload) => handlersRef.current.onScore(p));
    socket.on("activity:notify", (p: ActivityPayload) => handlersRef.current.onActivity(p));
    socket.on("question:unlocked", (p: QuestionUnlockedPayload) =>
      handlersRef.current.onQuestionUnlocked(p)
    );
    socket.on("event:stage", (p: EventStagePayload) =>
      handlersRef.current.onEventStage(p)
    );
    socket.on("time:tick", (p: TimeTickPayload) => handlersRef.current.onTimeTick(p));
    socket.on("sync:full", (p: SyncFullPayload) => handlersRef.current.onSyncFull(p));

    return () => {
      socket.removeAllListeners();
      socket.close();
      socketRef.current = null;
      setConnected(false);
    };
  }, [enabled]);

  return {
    connected,
    browserOnline,
    resync: () => socketRef.current?.emit("resync"),
  };
}
